import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Switch,
  FormControlLabel,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { getProducts, deleteProduct } from '../../services/productService';
import { Product } from '../../types/product';

interface FilterOptions {
  status: string;
  hasVariants: boolean | null;
  createdAfter: string;
  createdBefore: string;
  minVariants: number;
  maxVariants: number;
}

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(1); // تخزين رقم الصفحة الحالي الفعلي (يبدأ من 1)
  
  // Filter drawer state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    status: '',
    hasVariants: null,
    createdAfter: '',
    createdBefore: '',
    minVariants: 0,
    maxVariants: 100
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({
    status: '',
    hasVariants: null,
    createdAfter: '',
    createdBefore: '',
    minVariants: 0,
    maxVariants: 100
  });
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Calculate active filters count
  useEffect(() => {
    let count = 0;
    if (appliedFilters.status) count++;
    if (appliedFilters.hasVariants !== null) count++;
    if (appliedFilters.createdAfter) count++;
    if (appliedFilters.createdBefore) count++;
    if (appliedFilters.minVariants > 0) count++;
    if (appliedFilters.maxVariants < 100) count++;
    setActiveFilterCount(count);
  }, [appliedFilters]);

  // Client-side filtering for variant-related filters
  const applyClientSideFilters = (products: Product[]) => {
    // إذا لم تكن هناك منتجات، إرجاع مصفوفة فارغة
    if (!products || !Array.isArray(products) || products.length === 0) {
      console.log('No products to filter');
      return [];
    }
    
    console.log(`Applying client-side filters to ${products.length} products`);
    
    // حساب عدد التصفيات النشطة
    let activeFilterCount = 0;
    if (appliedFilters.hasVariants !== null) activeFilterCount++;
    if (appliedFilters.minVariants > 0) activeFilterCount++;
    if (appliedFilters.maxVariants < 100) activeFilterCount++;
    if (appliedFilters.createdAfter) activeFilterCount++;
    if (appliedFilters.createdBefore) activeFilterCount++;
    
    // إذا لم تكن هناك تصفيات نشطة، إرجاع المنتجات كما هي
    if (activeFilterCount === 0) {
      console.log('No active client-side filters, returning all products');
      return products;
    }
    
    const filteredProducts = products.filter(product => {
      const variantCount = product.variants?.length || 0;
      
      // Filter by variant count
      if (appliedFilters.hasVariants !== null) {
        if (appliedFilters.hasVariants && variantCount === 0) return false;
        if (!appliedFilters.hasVariants && variantCount > 0) return false;
      }
      
      if (variantCount < appliedFilters.minVariants) return false;
      if (appliedFilters.maxVariants < 100 && variantCount > appliedFilters.maxVariants) return false;
      
      // Filter by creation date
      if (appliedFilters.createdAfter && product.createdAt) {
        const createdDate = new Date(product.createdAt);
        const afterDate = new Date(appliedFilters.createdAfter);
        if (createdDate < afterDate) return false;
      }
      
      if (appliedFilters.createdBefore && product.createdAt) {
        const createdDate = new Date(product.createdAt);
        const beforeDate = new Date(appliedFilters.createdBefore);
        if (createdDate > beforeDate) return false;
      }
      
      return true;
    });
    
    console.log(`Client-side filtering removed ${products.length - filteredProducts.length} products`);
    return filteredProducts;
  };

  const fetchProducts = async (): Promise<void> => {
    // تعيين حالة التحميل وتنظيف المنتجات القديمة
    setLoading(true);
    setProducts([]);
    try {
      console.log(`جلب المنتجات - الصفحة: ${currentPageNumber}, عدد الصفوف: ${rowsPerPage}`);
      
      // إجراء طلب الحصول على البيانات مع تعطيل التخزين المؤقت تماماً
      const data = await getProducts(currentPageNumber, rowsPerPage, search, appliedFilters.status);
      
      console.log(`تم استلام ${data.products?.length || 0} منتج للصفحة ${currentPageNumber}`);
      
      if (data.products && data.products.length > 0) {
        console.log('أول منتج مستلم:', {
          id: data.products[0]._id,
          name: data.products[0].name
        });
      }
      
      // تطبيق التصفية الجانبية على البيانات الجديدة
      const filteredProducts = applyClientSideFilters(data.products);
      
      console.log(`بعد التصفية: ${filteredProducts.length} منتج (من أصل ${data.products.length})`);
      
      // تعيين البيانات الجديدة
      setProducts(filteredProducts);
      
      // تحديث معلومات الصفحات
      setTotalProducts(data.pagination.total);
      setTotalPages(data.pagination.pages);
      console.log(`معلومات الصفحات - الإجمالي: ${data.pagination.total}, الصفحات: ${data.pagination.pages}, الصفحة الحالية: ${currentPageNumber}`);
      setError(null);
    } catch (err) {
      setError('حدث خطأ أثناء جلب المنتجات');
      console.error('تفاصيل الخطأ:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // تأثير للتحميل عند تغيير الصفحة أو معايير البحث أو الفلترة
  useEffect(() => {
    console.log("تحميل البيانات - الصفحة:", currentPageNumber);
    fetchProducts();
  }, [currentPageNumber, rowsPerPage, search, appliedFilters.status]);

  const handleViewProduct = (id: string) => {
    navigate(`/products/${id}`);
  };

  const handleEditProduct = (id: string) => {
    navigate(`/products/edit/${id}`);
  };

  const handleDeleteProduct = async (id: string) => {
    if (window.confirm('هل أنت متأكد من رغبتك في حذف هذا المنتج؟')) {
      try {
        await deleteProduct(id);
        fetchProducts();
      } catch (err) {
        setError('حدث خطأ أثناء حذف المنتج');
        console.error(err);
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    console.log(`تغيير الصفحة من ${page} إلى ${newPage} (صفحة API: ${newPage + 1})`);
    
    // تحديث قيم الصفحة - useEffect سيتولى جلب البيانات تلقائياً
    setPage(newPage);
    setCurrentPageNumber(newPage + 1);
    
    console.log(`الحالة الجديدة - page: ${newPage}, currentPageNumber: ${newPage + 1}`);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log(`Changing rows per page from ${rowsPerPage} to ${newRowsPerPage}`);
    setRowsPerPage(newRowsPerPage);
    
    // إعادة تعيين الصفحة إلى الأولى عند تغيير عدد الصفوف
    setPage(0);
    setCurrentPageNumber(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
    setCurrentPageNumber(1);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    const newStatus = event.target.value as string;
    setStatus(newStatus);
    setFilterOptions(prev => ({
      ...prev,
      status: newStatus
    }));
    setAppliedFilters(prev => ({
      ...prev,
      status: newStatus
    }));
    setPage(0);
    setCurrentPageNumber(1);
  };
  
  // Filter drawer handlers
  const toggleFilterDrawer = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };
  
  const handleFilterOptionChange = (option: keyof FilterOptions, value: any) => {
    setFilterOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };
  
  const applyFilters = () => {
    setAppliedFilters(filterOptions);
    setPage(0);
    setCurrentPageNumber(1);
    setFilterDrawerOpen(false);
  };
  
  const resetFilters = () => {
    const defaultFilters: FilterOptions = {
      status: '',
      hasVariants: null,
      createdAfter: '',
      createdBefore: '',
      minVariants: 0,
      maxVariants: 100
    };
    setFilterOptions(defaultFilters);
    setAppliedFilters(defaultFilters);
    setStatus('');
    setPage(0);
    setCurrentPageNumber(1);
    setFilterDrawerOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'discontinued':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'نشط';
      case 'inactive':
        return 'غير نشط';
      case 'discontinued':
        return 'متوقف';
      default:
        return status;
    }
  };

  const getVariantsCount = (product: Product) => {
    return product.variants && Array.isArray(product.variants) ? product.variants.length : 0;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          قائمة المنتجات
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/products/new')}
          >
            إضافة منتج جديد
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', mb: 3, gap: 2 }}>
        <TextField
          label="بحث"
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <Select
            value={status}
            onChange={handleStatusChange}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
          >
            <MenuItem value="">
              <em>جميع الحالات</em>
            </MenuItem>
            <MenuItem value="active">نشط</MenuItem>
            <MenuItem value="inactive">غير نشط</MenuItem>
            <MenuItem value="discontinued">متوقف</MenuItem>
          </Select>
        </FormControl>
        
        <Tooltip title="تصفية متقدمة">
          <Badge badgeContent={activeFilterCount} color="primary" sx={{ alignSelf: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={toggleFilterDrawer}
              color={activeFilterCount > 0 ? "primary" : "inherit"}
            >
              تصفية
            </Button>
          </Badge>
        </Tooltip>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      
      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={toggleFilterDrawer}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 }, p: 2 }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">تصفية المنتجات</Typography>
          <IconButton onClick={toggleFilterDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <List sx={{ mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<FilterIcon />}>
              <Typography>حالة المنتج</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={filterOptions.status}
                  onChange={(e) => handleFilterOptionChange('status', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>جميع الحالات</em>
                  </MenuItem>
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                  <MenuItem value="discontinued">متوقف</MenuItem>
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<FilterIcon />}>
              <Typography>المتغيرات</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={filterOptions.hasVariants === true}
                    onChange={(e) =>
                      handleFilterOptionChange(
                        'hasVariants',
                        e.target.checked ? true : e.target.checked === false ? false : null
                      )
                    }
                  />
                }
                label="لديه متغيرات فقط"
                sx={{ mb: 2 }}
              />
              
              <Typography gutterBottom>عدد المتغيرات</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="الحد الأدنى"
                    type="number"
                    value={filterOptions.minVariants}
                    onChange={(e) => handleFilterOptionChange('minVariants', Number(e.target.value))}
                    fullWidth
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="الحد الأقصى"
                    type="number"
                    value={filterOptions.maxVariants}
                    onChange={(e) => handleFilterOptionChange('maxVariants', Number(e.target.value))}
                    fullWidth
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<FilterIcon />}>
              <Typography>تاريخ الإنشاء</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="من تاريخ"
                    type="date"
                    value={filterOptions.createdAfter}
                    onChange={(e) => handleFilterOptionChange('createdAfter', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="إلى تاريخ"
                    type="date"
                    value={filterOptions.createdBefore}
                    onChange={(e) => handleFilterOptionChange('createdBefore', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </List>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={resetFilters}
            startIcon={<ClearIcon />}
          >
            إعادة تعيين
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={applyFilters}
            startIcon={<FilterIcon />}
          >
            تطبيق التصفية
          </Button>
        </Box>
      </Drawer>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="products table">
          <TableHead>
            <TableRow>
              <TableCell>الاسم</TableCell>
              <TableCell>الوصف</TableCell>
              <TableCell>عدد المتغيرات</TableCell>
              <TableCell>الخصائص المستخدمة</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>تاريخ الإنشاء</TableCell>
              <TableCell align="center">إجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <Typography>جاري تحميل المنتجات...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>لا توجد منتجات متاحة في الصفحة {currentPageNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search ? `لا توجد نتائج تطابق البحث: "${search}"` : ''}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => {
                // حساب الخصائص المستخدمة فعلياً في المتغيرات
                const getUsedAttributes = () => {
                  if (!product.variants || product.variants.length === 0) {
                    return '-';
                  }

                  // جمع جميع القيم الفريدة لكل خاصية
                  const attributeValues: Record<string, Set<string>> = {};

                  product.variants.forEach(variant => {
                    if (variant.attributes) {
                      // تحويل Map إلى Object إذا لزم الأمر
                      const attrs = variant.attributes instanceof Map
                        ? Object.fromEntries(variant.attributes)
                        : variant.attributes;

                      Object.entries(attrs).forEach(([key, value]) => {
                        if (!attributeValues[key]) {
                          attributeValues[key] = new Set();
                        }
                        if (value) {
                          attributeValues[key].add(String(value));
                        }
                      });
                    }
                  });

                  // تنسيق النتيجة
                  const result = Object.entries(attributeValues)
                    .map(([key, values]) => `${key}: ${values.size}`)
                    .join(', ');

                  return result || '-';
                };

                return (
                  <TableRow key={product._id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      {product.description
                        ? product.description.length > 50
                          ? `${product.description.substring(0, 50)}...`
                          : product.description
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.variants && product.variants.length > 0 ? product.variants.length : 0}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {getUsedAttributes()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                    <Chip
                      label={getStatusText(product.status)}
                      color={getStatusColor(product.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {product.createdAt
                      ? new Date(product.createdAt).toLocaleDateString('ar-SA')
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleViewProduct(product._id!)}
                      title="عرض"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditProduct(product._id!)}
                      title="تعديل"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteProduct(product._id!)}
                      title="حذف"
                    >
                      <DeleteIcon />
                    </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* مكون التنقل بين الصفحات مع تعزيز التفاعلية */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalProducts}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="عدد العناصر في الصفحة:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`} (الصفحة ${currentPageNumber} من ${totalPages})`
        }
        showFirstButton
        showLastButton
        sx={{
          '.MuiTablePagination-actions': {
            marginLeft: 2,
            marginRight: 2,
          },
          '.MuiTablePagination-selectLabel': {
            marginRight: 2,
          },
          button: {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
            '&:disabled': {
              opacity: 0.5,
            },
            padding: '8px',
            minWidth: '40px',
          }
        }}
        nextIconButtonProps={{
          'aria-label': 'الصفحة التالية',
          title: 'الصفحة التالية',
          style: { color: page < totalPages - 1 ? 'primary' : 'disabled' }
        }}
        backIconButtonProps={{
          'aria-label': 'الصفحة السابقة',
          title: 'الصفحة السابقة',
          style: { color: page > 0 ? 'primary' : 'disabled' }
        }}
        getItemAriaLabel={(type) => {
          return type === 'first' ? 'الصفحة الأولى' :
                 type === 'last' ? 'الصفحة الأخيرة' :
                 type === 'next' ? 'الصفحة التالية' :
                 'الصفحة السابقة';
        }}
      />
    </Box>
  );
};

export default ProductList;